import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { paths } from '@/app/router/paths'
import { env } from '@/config/env'
import { useSessionStore } from '@/features/application-session'
import { signOutOfficerAndLeave } from '@/lib/auth/officerSession'
import { useApiErrorMessage } from '@/shared/hooks/useApiErrorMessage'
import { Alert } from '@/shared/ui/Alert'
import { Button } from '@/shared/ui/Button'
import { Dialog } from '@/shared/ui/Dialog'
import { useCancelApplication } from '../api/cancelApplication.api'

/**
 * The confirm dialog (board 2182:27606) has no reason field — it asks the citizen
 * to confirm, not to explain — but §8.1's `Reason` is mandatory free text. This
 * fixed value fills that field without adding a control the design doesn't have.
 */
const CANCEL_REASON = 'Cancelled by the applicant from the citizen portal.'

/**
 * The "Cancel Application" affordance from board 2173:30549 — the POPIA guarantee
 * that a citizen can abandon an application and take their details with them.
 *
 * Rendered by `PublicLayout` on the SUBMITTED receipt only. It used to sit in
 * that header slot for every wizard step; mid-journey the slot now holds "Reset
 * Application", which clears the session locally without waiting on a network
 * call. That leaves cancel where it is the only thing that still does any work:
 * once §4.10 has finalised the application, clearing the session locally cannot
 * withdraw it, and §8.1 is the citizen's only route to doing so.
 *
 * The docstring this replaced claimed the button was "hidden once the application
 * is submitted". The route gate never did that — it matched every wizard step —
 * and submitted is in fact the one screen it is now restricted TO.
 *
 * A successful cancel leaves the way the receipt's own "Return Home" button leaves:
 * session cleared, browser at `/`, officer signed out of Entra. It used to land on
 * the ID-number screen and sign nobody out — the only terminal exit in the app that
 * did not, which at a shared counter meant the next application could open with the
 * previous officer still cached and be recorded under their AgentID.
 */
export function CancelApplicationButton() {
  const { t } = useTranslation(['apply', 'common'])
  const navigate = useNavigate()
  const messageFor = useApiErrorMessage()

  const appRefNo = useSessionStore((s) => s.appRefNo)
  const idNo = useSessionStore((s) => s.idNo)
  const clear = useSessionStore((s) => s.clear)

  const [open, setOpen] = useState(false)
  /**
   * Set when Confirm is pressed with no ID number to send.
   *
   * `clearApplicantData()` keeps `idNo` on the receipt precisely so this cannot
   * happen, but two things still reach here without one: a session persisted before
   * that carve-out shipped, and a deployment with `VITE_PERSIST_APPLICANT_DETAILS`
   * turned off, where a refresh of the receipt rehydrates without it.
   *
   * Local state rather than letting the mutation throw, because the two are not the
   * same failure. A thrown Error renders as `errors:api.unknown` — "Something went
   * wrong. Please try again." — which is both untrue (nothing went wrong; we cannot
   * ask) and useless advice (trying again cannot help). The button stays visible and
   * explains itself instead.
   */
  const [blocked, setBlocked] = useState(false)
  const mutation = useCancelApplication(appRefNo)

  const closeDialog = () => {
    if (mutation.isPending) return
    mutation.reset()
    setBlocked(false)
    setOpen(false)
  }

  const onConfirm = () => {
    if (!idNo) {
      setBlocked(true)
      return
    }

    mutation.mutate(CANCEL_REASON, {
      // Cleared and navigated together, same as the physical-verification "No"
      // flow: the router has already left the wizard before RequireSession can
      // re-render and catch an empty store. `clear()` also unmounts this component
      // through the `!appRefNo` guard below, and Dialog closes itself on unmount.
      //
      // The Entra sign-out comes last and unawaited, for the reason SubmittedPage's
      // Return Home gives: it is a page navigation that has to load MSAL first, so
      // awaiting it would hold this handler open against a store already emptied.
      onSuccess: () => {
        setOpen(false)
        clear()
        void navigate(paths.home, { replace: true })
        void signOutOfficerAndLeave()
      },

      // The alert below says "Something went wrong" for anything that is not an
      // ApiError, which is exactly what a guard throwing before the wire looks
      // like — no request, no console entry, nothing to go on. That state shipped
      // once (the ID-number guard removed on 2026-09-10); this is what makes the
      // next one a one-glance diagnosis instead of a bisect.
      onError: (cause) => {
        if (!env.isProduction) console.error('[cancel] §8.1 cancel failed', cause)
      },
    })
  }

  if (!appRefNo) return null

  return (
    <>
      <Button
        variant="secondary"
        size="lg"
        className="border-action-text text-action-text hover:bg-primary-8 hover:text-action-text"
        onClick={() => setOpen(true)}
      >
        {t('apply:cancelApplication.trigger')}
      </Button>

      <Dialog
        open={open}
        onClose={closeDialog}
        title={t('apply:cancelApplication.dialog.title')}
        description={t('apply:cancelApplication.dialog.body')}
        body={
          blocked ? (
            <Alert tone="error">{t('apply:cancelApplication.dialog.unavailable')}</Alert>
          ) : mutation.isError ? (
            <Alert tone="error">{messageFor(mutation.error)}</Alert>
          ) : undefined
        }
      >
        <Button
          variant="secondary"
          className="rounded-btn border-hairline text-body2 hover:bg-page hover:text-ink-900"
          onClick={closeDialog}
          disabled={mutation.isPending}
        >
          {t('apply:cancelApplication.dialog.goBack')}
        </Button>
        {/* Disabled only AFTER the citizen has been told why — pressing Confirm is
            what surfaces the explanation, so it cannot start out unavailable. */}
        <Button
          className="rounded-btn text-body2 font-semibold"
          onClick={onConfirm}
          loading={mutation.isPending}
          disabled={blocked}
        >
          {t('apply:cancelApplication.dialog.confirm')}
        </Button>
      </Dialog>
    </>
  )
}
